// @ts-check
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {uploadPromotionMedia} from '@/services/PromotionsService/promotions'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {useCallback} from 'react'

/**
 * Handles banner image upload for the promotion display step.
 * Provides an immediate local preview, calls the upload API, and handles errors.
 */
export const usePromotionBannerUpload = () => {
  const {promotion, updatePromotion} = usePromotion()
  const dispatch = useAppDispatch()

  const handleBannerUpload = useCallback(
    async file => {
      if (!file) {
        updatePromotion(prev => ({...prev, bannerUrl: '', bannerFile: null}))
        return
      }

      // Show local preview immediately while upload is in-flight
      const previewUrl = URL.createObjectURL(file)
      updatePromotion(prev => ({...prev, bannerUrl: previewUrl, bannerFile: file}))

      if (!promotion?.id) {
        console.error('No promotion id available for uploading media.')
        return
      }

      const metadata = {
        mediaUploadMetadata: [
          {
            id: promotion.id,
            originalFileName: file.name,
            originalFormat: file.type.split('/')[1],
            originalSize: file.size,
            blobFileSize: file.size,
            blobFormat: file.type.split('/')[1],
            referenceEntity: 'promotion',
          },
        ],
      }

      const formData = new FormData()
      formData.append('files', file)
      formData.append('metadata', JSON.stringify(metadata))

      try {
        const response = await uploadPromotionMedia(promotion.id, formData)
        const bannerUrl = response?.data?.[0]?.imageCDNUrl
        updatePromotion(prev => ({...prev, bannerUrl, bannerFile: null}))
      } catch (error) {
        let errorMessage = 'Error uploading image'

        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error?.data?.message) {
          errorMessage = error.data.message
        } else if (error?.message) {
          errorMessage = error.message
        }

        if (
          errorMessage.toLowerCase().includes('filename') ||
          errorMessage.toLowerCase().includes('name') ||
          errorMessage.toLowerCase().includes('invalid')
        ) {
          errorMessage =
            'Filename contains special characters that are not supported. Please rename your file to use only letters, numbers, hyphens, and underscores.'
        }

        dispatch(setToastNotifications({show: true, message: errorMessage, type: 'error'}))
        updatePromotion(prev => ({...prev, bannerUrl: '', bannerFile: null}))
      }
    },
    [promotion?.id, updatePromotion, dispatch]
  )

  return {handleBannerUpload}
}
